import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Send, Phone, Mail, Smartphone, Check, CheckCheck, Trash2, ExternalLink, Settings } from 'lucide-react';

export default function NotificationsPage() {
  const { user, axiosAuth } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [prefs, setPrefs] = useState({
    sms_enabled: false, phone_number: '',
    appointment_reminders: true, checkin_reminders: true, crisis_alerts: true
  });
  const [smsConfig, setSmsConfig] = useState({ provider: 'none', account_sid: '', auth_token: '', from_number: '' });
  const [emailConfig, setEmailConfig] = useState({ provider: 'none', api_key: '', from_email: '' });
  const [pushConfig, setPushConfig] = useState({ provider: 'none', server_key: '', project_id: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox');
  const api = axiosAuth();
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchNotifications();
    api.get('/notifications/preferences').then(r => setPrefs(r.data)).catch(() => {});
    if (isAdmin) {
      api.get('/settings/sms').then(r => setSmsConfig(s => ({ ...s, ...r.data, provider: r.data.provider || 'none' }))).catch(() => {});
      api.get('/settings/email').then(r => setEmailConfig(s => ({ ...s, ...r.data, provider: r.data.provider || 'none' }))).catch(() => {});
      api.get('/settings/push').then(r => setPushConfig(s => ({ ...s, ...r.data, provider: r.data.provider || 'none' }))).catch(() => {});
    }
  }, []);

  const fetchNotifications = async () => {
    try {
      const r = await api.get('/notifications?limit=100');
      setNotifications(r.data.notifications);
      setUnreadCount(r.data.unread_count);
    } catch {}
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(n => n.map(x => x.id === id ? { ...x, read: true } : x));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(n => n.map(x => ({ ...x, read: true })));
    setUnreadCount(0);
    toast.success('All notifications marked as read');
  };

  const deleteNotif = async (id, wasUnread) => {
    await api.delete(`/notifications/${id}`);
    setNotifications(n => n.filter(x => x.id !== id));
    if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));
  };

  const savePrefs = async () => {
    setSaving(true);
    try { await api.put('/notifications/preferences', prefs); toast.success('Preferences saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveSmsConfig = async () => {
    try { await api.put('/settings/sms', smsConfig); toast.success('SMS configuration saved!'); }
    catch { toast.error('Failed to save'); }
  };

  const saveEmailConfig = async () => {
    try { await api.put('/settings/email', emailConfig); toast.success('Email configuration saved!'); }
    catch { toast.error('Failed to save'); }
  };

  const savePushConfig = async () => {
    try { await api.put('/settings/push', pushConfig); toast.success('Push configuration saved!'); }
    catch { toast.error('Failed to save'); }
  };

  const sendTest = async () => {
    try { const r = await api.post('/notifications/send-test'); toast.info(r.data.message); fetchNotifications(); }
    catch { toast.error('Test failed'); }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Notifications</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck size={14} /> Mark All Read
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="inbox" className="rounded-full text-xs gap-1.5">
            <Bell size={14} /> Inbox {unreadCount > 0 && <Badge variant="destructive" className="h-4 text-[10px] px-1.5">{unreadCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="preferences" className="rounded-full text-xs gap-1.5"><Phone size={14} /> Preferences</TabsTrigger>
          {isAdmin && <TabsTrigger value="channels" className="rounded-full text-xs gap-1.5"><Settings size={14} /> Channel Config</TabsTrigger>}
        </TabsList>

        {/* INBOX TAB */}
        <TabsContent value="inbox">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-0">
              {notifications.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell size={40} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm text-muted-foreground">No notifications yet</p>
                  <p className="text-xs text-muted-foreground mt-1">You'll see updates about appointments, approvals, and more here.</p>
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {notifications.map(n => (
                    <div key={n.id} className={`flex items-start gap-3 px-5 py-4 transition-colors ${n.read ? 'bg-white' : 'bg-teal-50/30'}`} data-testid={`notif-item-${n.id}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${n.read ? 'bg-slate-100' : 'bg-teal-100'}`}>
                        <Bell size={15} className={n.read ? 'text-slate-400' : 'text-teal-600'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-semibold ${n.read ? 'text-slate-500' : 'text-teal-700'}`}>{n.title}</span>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />}
                        </div>
                        <p className={`text-sm leading-snug ${n.read ? 'text-slate-500' : 'text-slate-700'}`}>{n.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-1.5">{timeAgo(n.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 pt-1">
                        {!n.read && (
                          <button onClick={() => markRead(n.id)} className="p-1.5 rounded-lg hover:bg-teal-100 transition-colors" title="Mark read">
                            <Check size={13} className="text-teal-600" />
                          </button>
                        )}
                        <button onClick={() => deleteNotif(n.id, !n.read)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete">
                          <Trash2 size={13} className="text-slate-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREFERENCES TAB */}
        <TabsContent value="preferences">
          <Card className="border-0 shadow-sm rounded-2xl max-w-lg">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Notification Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                <div className="flex items-center gap-3"><Phone size={18} className="text-teal-600" /><div><Label className="text-sm font-medium">Enable SMS Notifications</Label><p className="text-xs text-muted-foreground">Receive text message alerts</p></div></div>
                <Switch checked={prefs.sms_enabled} onCheckedChange={v => setPrefs({ ...prefs, sms_enabled: v })} data-testid="sms-toggle" />
              </div>
              {prefs.sms_enabled && (
                <>
                  <div className="space-y-1.5"><Label className="text-sm">Phone Number</Label><Input value={prefs.phone_number} onChange={e => setPrefs({ ...prefs, phone_number: e.target.value })} placeholder="(555) 123-4567" data-testid="sms-phone-input" /></div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30"><Label className="text-sm">Appointment Reminders</Label><Switch checked={prefs.appointment_reminders} onCheckedChange={v => setPrefs({ ...prefs, appointment_reminders: v })} /></div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30"><Label className="text-sm">Check-in Reminders</Label><Switch checked={prefs.checkin_reminders} onCheckedChange={v => setPrefs({ ...prefs, checkin_reminders: v })} /></div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30"><Label className="text-sm">Crisis Alerts</Label><Switch checked={prefs.crisis_alerts} onCheckedChange={v => setPrefs({ ...prefs, crisis_alerts: v })} /></div>
                </>
              )}
              <Button onClick={savePrefs} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-notification-prefs">{saving ? 'Saving...' : 'Save Preferences'}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHANNEL CONFIG TAB (Admin Only) */}
        {isAdmin && (
          <TabsContent value="channels">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* SMS Provider */}
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Phone size={18} className="text-teal-600" />
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>SMS Provider</CardTitle>
                  </div>
                  <Badge variant={smsConfig.provider !== 'none' ? 'default' : 'secondary'} className="w-fit text-[10px]">
                    {smsConfig.provider !== 'none' ? 'Configured' : 'Not Configured'}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Provider</Label>
                    <Select value={smsConfig.provider} onValueChange={v => setSmsConfig({ ...smsConfig, provider: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Configured</SelectItem>
                        <SelectItem value="telnyx">Telnyx</SelectItem>
                        <SelectItem value="twilio">Twilio</SelectItem>
                        <SelectItem value="plivo">Plivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {smsConfig.provider !== 'none' && (
                    <>
                      <div className="space-y-1"><Label className="text-xs">Account SID / API Key</Label><Input type="password" value={smsConfig.account_sid} onChange={e => setSmsConfig({ ...smsConfig, account_sid: e.target.value })} className="h-9 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Auth Token / API Secret</Label><Input type="password" value={smsConfig.auth_token} onChange={e => setSmsConfig({ ...smsConfig, auth_token: e.target.value })} className="h-9 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">From Phone Number</Label><Input value={smsConfig.from_number} onChange={e => setSmsConfig({ ...smsConfig, from_number: e.target.value })} placeholder="+1234567890" className="h-9 text-sm" /></div>
                    </>
                  )}
                  <Button onClick={saveSmsConfig} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full h-9 text-xs">Save SMS Config</Button>
                </CardContent>
              </Card>

              {/* Email Provider */}
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Mail size={18} className="text-blue-600" />
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Email Provider</CardTitle>
                  </div>
                  <Badge variant={emailConfig.provider !== 'none' ? 'default' : 'secondary'} className="w-fit text-[10px]">
                    {emailConfig.provider !== 'none' ? 'Configured' : 'Not Configured'}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Provider</Label>
                    <Select value={emailConfig.provider} onValueChange={v => setEmailConfig({ ...emailConfig, provider: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Configured</SelectItem>
                        <SelectItem value="sendgrid">SendGrid</SelectItem>
                        <SelectItem value="resend">Resend</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {emailConfig.provider !== 'none' && (
                    <>
                      <div className="space-y-1"><Label className="text-xs">API Key</Label><Input type="password" value={emailConfig.api_key} onChange={e => setEmailConfig({ ...emailConfig, api_key: e.target.value })} className="h-9 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">From Email</Label><Input value={emailConfig.from_email} onChange={e => setEmailConfig({ ...emailConfig, from_email: e.target.value })} placeholder="noreply@yourorg.org" className="h-9 text-sm" /></div>
                    </>
                  )}
                  <Button onClick={saveEmailConfig} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-9 text-xs">Save Email Config</Button>
                </CardContent>
              </Card>

              {/* Push Provider */}
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Smartphone size={18} className="text-violet-600" />
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Push Notifications</CardTitle>
                  </div>
                  <Badge variant={pushConfig.provider !== 'none' ? 'default' : 'secondary'} className="w-fit text-[10px]">
                    {pushConfig.provider !== 'none' ? 'Configured' : 'Not Configured'}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Provider</Label>
                    <Select value={pushConfig.provider} onValueChange={v => setPushConfig({ ...pushConfig, provider: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Configured</SelectItem>
                        <SelectItem value="fcm">Firebase Cloud Messaging</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {pushConfig.provider !== 'none' && (
                    <>
                      <div className="space-y-1"><Label className="text-xs">Server Key</Label><Input type="password" value={pushConfig.server_key} onChange={e => setPushConfig({ ...pushConfig, server_key: e.target.value })} className="h-9 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Project ID</Label><Input value={pushConfig.project_id} onChange={e => setPushConfig({ ...pushConfig, project_id: e.target.value })} className="h-9 text-sm" /></div>
                    </>
                  )}
                  <Button onClick={savePushConfig} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-9 text-xs">Save Push Config</Button>
                </CardContent>
              </Card>
            </div>

            {/* Test & Signup Info */}
            <div className="grid gap-6 md:grid-cols-2 mt-6">
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Test Notifications</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">Send a test notification through all configured channels to verify your setup.</p>
                  <Button onClick={sendTest} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="send-test-notification">
                    <Send size={14} /> Send Test Notification
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Where to Get API Keys</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { label: 'Telnyx (SMS)', url: 'https://telnyx.com', desc: 'Cheapest SMS. Sign up, get API key + phone number.' },
                    { label: 'Twilio (SMS)', url: 'https://twilio.com', desc: 'Popular SMS provider. Free trial available.' },
                    { label: 'SendGrid (Email)', url: 'https://sendgrid.com', desc: 'Free tier: 100 emails/day. Create API key in Settings.' },
                    { label: 'Resend (Email)', url: 'https://resend.com', desc: 'Modern email API. Free tier: 3000 emails/month.' },
                    { label: 'Firebase (Push)', url: 'https://console.firebase.google.com', desc: 'Create project, enable Cloud Messaging, get server key.' },
                  ].map(item => (
                    <a key={item.label} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors group">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-700 group-hover:text-teal-700">{item.label}</p>
                        <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                      </div>
                      <ExternalLink size={12} className="text-slate-400 group-hover:text-teal-600 shrink-0" />
                    </a>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
